import { useState, useEffect } from "react";
import {
  Search,
  Download,
  Filter,
  Mail,
  Phone,
  Building,
  Tag,
  Eye,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_lead_enquiry_data,
} from "../serviceconnection/ServiceConnection";

const statusColors = {
  Demo: { bg: "hsl(var(--info-muted))", text: "hsl(var(--info))" },
  Consultation: {
    bg: "hsl(var(--success-muted))",
    text: "hsl(var(--success))",
  },
};

const getStatus = (val) => (val === 1 ? "Demo" : "Consultation");

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [viewLead, setViewLead] = useState(null);
  const [listLoading, setListLoading] = useState(true);

  const filtered = leads.filter(
    (l) =>
      (l.first_name.toLowerCase().includes(search.toLowerCase()) ||
        l.last_name.toLowerCase().includes(search.toLowerCase()) ||
        l.company.toLowerCase().includes(search.toLowerCase())) &&
      (statusFilter === "All" ||
        getStatus(l.demo_consultation) === statusFilter),
  );

  const handleExport = () => {
    const headers = "Name,Email,Company,Role,Source,Date,Status";
    const rows = filtered
      .map(
        (l) =>
          `${l.first_name} ${l.last_name},${l.email},${l.company},${l.role},${l.from_page},${l.entry_date},${getStatus(l.demo_consultation)}`,
      )
      .join("\n");
    const csv = `${headers}\n${rows}`;
    console.log("CSV Export:\n", csv);
    showSuccess(
      "CSV exported!",
      "Check the browser console for the CSV output.",
    );
  };

  const fetchSolutions = async () => {
    try {
      setListLoading(true);
      const formData = new FormData();
      const response = await server_post_data(get_lead_enquiry_data, formData);
      setLeads(response.data?.data ?? []);
    } catch (err) {
      console.error("Failed to fetch solutions:", err);
      showError("Failed to load solutions:", err);
    } finally {
      setListLoading(false);
    }
  };
  useEffect(() => {
    fetchSolutions();
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">
            Leads captured from gated resources and event registrations
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 text-sm font-semibold border border-border rounded-lg px-4 py-2 text-foreground hover:bg-muted transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Total Leads",
            value: leads.length,
            color: "hsl(var(--accent))",
          },
          {
            label: "Demo",
            value: leads.filter((l) => l.demo_consultation === 1).length,
            color: "hsl(var(--info))",
          },
          {
            label: "Consultation",
            value: leads.filter((l) => l.demo_consultation !== 1).length,
            color: "hsl(var(--success))",
          },
          {
            label: "This Month",
            value: leads.filter((l) => {
              const d = new Date(l.entry_date);
              const now = new Date();
              return (
                d.getMonth() === now.getMonth() &&
                d.getFullYear() === now.getFullYear()
              );
            }).length,
            color: "hsl(var(--warning))",
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="cms-card p-4 flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or company..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        {["All", "Demo", "Consultation"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${statusFilter === s ? "text-white border-transparent" : "border-border text-muted-foreground hover:border-accent/30"}`}
            style={
              statusFilter === s ? { background: "hsl(var(--accent))" } : {}
            }
          >
            {s}
          </button>
        ))}
      </div>

      {/* Lead Detail Modal */}
      {viewLead && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setViewLead(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-md mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Lead Details</h2>
              <button
                onClick={() => setViewLead(null)}
                className="text-muted-foreground hover:text-foreground text-xs font-medium"
              >
                Close
              </button>
            </div>
            <div className="section-panel-body space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ background: "var(--gradient-hero)" }}
                >
                  {`${viewLead.first_name} ${viewLead.last_name}`
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    {viewLead.first_name} {viewLead.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {viewLead.email}
                  </p>
                </div>
              </div>
              {[
                ["Company", viewLead.company],
                ["Role", viewLead.role],
                ["Industry", viewLead.industry],
                ["Interest", viewLead.interest],
                ["Source", viewLead.from_page],
                ["Phone", viewLead.phone],
                ["Message", viewLead.message],
                ["Date", viewLead.entry_date?.slice(0, 10)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium text-foreground">{v}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-border">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Request for
                </p>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{
                    background:
                      statusColors[getStatus(viewLead.demo_consultation)]?.bg,
                    color:
                      statusColors[getStatus(viewLead.demo_consultation)]?.text,
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  {getStatus(viewLead.demo_consultation)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="section-panel">
        <div className="overflow-x-auto">
          <table className="w-full cms-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Role</th>
                <th>Lead Source</th>
                <th>Date</th>
                <th>Category</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="group">
                  <td>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: "var(--gradient-hero)" }}
                      >
                        {`${l.first_name} ${l.last_name}`
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground text-sm">
                          {l.first_name} {l.last_name}
                        </p>
                        <p className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {l.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Building className="w-3.5 h-3.5" />
                      {l.company}
                    </span>
                  </td>
                  <td className="text-muted-foreground">{l.role}</td>
                  <td>
                    <span className="flex items-center gap-1.5 text-xs">
                      <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-foreground font-medium truncate max-w-[160px]">
                        {l.from_page}
                      </span>
                    </span>
                  </td>
                  <td className="text-muted-foreground text-sm">
                    {l.entry_date?.slice(0, 10)}
                  </td>
                  <td>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                      style={{
                        background:
                          statusColors[getStatus(l.demo_consultation)]?.bg,
                        color:
                          statusColors[getStatus(l.demo_consultation)]?.text,
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {getStatus(l.demo_consultation)}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 opacity-100 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setViewLead(l)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* <button className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground">
                        <Phone className="w-4 h-4" />
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {leads.length} leads
          </p>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${n === 1 ? "text-white" : "text-muted-foreground hover:bg-muted"}`}
                style={n === 1 ? { background: "hsl(var(--accent))" } : {}}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
